import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { setToken } from "@/lib/api";
import { UsersRound } from "lucide-react";
import { t } from "@/i18n";

function fmtErr(d, fb = "Erreur.") {
  return typeof d === "string" ? d : d?.msg || fb;
}

const Section = ({ title, children }) => (
  <div className="rounded-2xl border bg-card p-5">
    <h2 className="font-head text-lg font-bold mb-4">{title}</h2>
    {children}
  </div>
);
const Field = (props) => (
  <input
    {...props}
    className="w-full px-4 py-2.5 rounded-xl border bg-background outline-none focus:ring-2 ring-primary mb-3"
  />
);

export default function Account() {
  const { user, refresh, logout } = useAuth();
  const nav = useNavigate();
  const [pw, setPw] = useState({ current: "", new: "" });
  const [em, setEm] = useState({ email: "", password: "" });
  const [goal, setGoal] = useState(user?.daily_goal || 10);
  const [confirmDel, setConfirmDel] = useState(false);

  const changePw = async () => {
    try {
      await api.post("/account/change-password", pw);
      toast.success("Mot de passe mis à jour.");
      setPw({ current: "", new: "" });
    } catch (e) {
      toast.error(fmtErr(e.response?.data?.detail));
    }
  };
  const changeEmail = async () => {
    try {
      await api.post("/account/change-email", em);
      await refresh();
      toast.success("Email mis à jour.");
      setEm({ email: "", password: "" });
    } catch (e) {
      toast.error(fmtErr(e.response?.data?.detail));
    }
  };
  const saveGoal = async () => {
    try {
      await api.post("/account/goal", { daily_goal: goal });
      await refresh();
      toast.success("Objectif quotidien mis à jour.");
    } catch {
      toast.error("Erreur.");
    }
  };
  const del = async () => {
    try {
      await api.delete("/account");
      setToken(null);
      toast.success("Compte supprimé.");
      window.location.href = "/auth";
    } catch (e) {
      toast.error(fmtErr(e.response?.data?.detail));
    }
  };
  const replayTuto = () => {
    localStorage.setItem("replayTutorial", "1");
    nav("/dashboard");
    window.location.reload();
  };

  return (
    <div className="max-w-xl mx-auto space-y-5">
      <h1 className="font-head text-3xl font-bold">{t("navbar.mon_compte")}</h1>

      <Section title={t("account.profil")}>
        <div className="text-sm text-muted-foreground mb-1">
          {t("account.nom")}{" "}
          <span className="text-foreground font-medium">{user?.name}</span>
        </div>
        <div className="text-sm text-muted-foreground">
          {t("account.email")}{" "}
          <span className="text-foreground font-medium">{user?.email}</span> {t("account.niveau")} {user?.level}
        </div>
      </Section>

      <Section title={t("account.abonnement_et_acces")}>
        <div className="rounded-xl bg-secondary/60 p-4 mb-3">
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="text-xs text-muted-foreground">{t("account.statut")}</div>
              <div className="font-head font-bold">
                {user?.account_type === "trial"
                  ? t("account.essai_gratuit")
                  : user?.account_type === "expired"
                    ? "Essai terminé"
                    : user?.account_type === "admin"
                      ? "Administrateur"
                      : `Accès ${user?.plan || "actif"}`}
              </div>
            </div>
            <button
              onClick={() => nav("/Pricing")}
              className="rounded-xl bg-primary px-3 py-2 text-sm font-semibold text-primary-foreground"
            >
              {user?.account_type === "expired"
                ? t("account.debloquer_mon_acces")
                : t("account.voir_les_formules")}
            </button>
          </div>
          {user?.account_type === "trial" && (
            <div className="mt-3 rounded-xl border border-primary/20 bg-primary/5 p-3">
              <p className="font-semibold text-sm">
                {user.access_days_remaining > 0
                  ? `Il te reste ${user.access_days_remaining} jour${user.access_days_remaining > 1 ? "s" : ""} d’essai gratuit.`
                  : t("account.ton_essai_gratuit_se_termine_aujourd")}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                {t("account.tu_peux_tout_tester_pendant_7")}
              </p>
              {user.access_expires_at && (
                <p className="text-xs text-muted-foreground mt-1">
                  {t("account.fin_de_l_essai")}{" "}
                  {new Date(user.access_expires_at).toLocaleDateString("fr-FR")}
                </p>
              )}
            </div>
          )}
          {(user?.account_type === "paid" || user?.account_type === "free") && (
            <div className="mt-3 rounded-xl border border-emerald-500/20 bg-emerald-500/5 p-3">
              <p className="font-semibold text-sm">
                {user.plan === "lifetime"
                  ? t("account.ton_acces_premium_est_valable_a")
                  : `Il te reste ${user.access_days_remaining ?? 0} jour${user.access_days_remaining === 1 ? "" : "s"} d’accès premium.`}
              </p>
              {user.access_expires_at && (
                <p className="text-xs text-muted-foreground mt-1">
                  {t("account.fin_de_l_acces")}{" "}
                  {new Date(user.access_expires_at).toLocaleDateString("fr-FR")}
                </p>
              )}
            </div>
          )}
          {user?.account_type === "expired" && (
            <div className="mt-3 rounded-xl border border-amber-500/30 bg-amber-500/10 p-3">
              <p className="font-semibold text-sm">
                {t("account.ta_periode_d_acces_est_terminee")}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                {t("account.le_site_reste_visible_mais_les")}
              </p>
            </div>
          )}
        </div>
        {user?.account_type === "paid" && (
          <div className="flex flex-wrap gap-2">
            <button
              onClick={async () => {
                try {
                  const { data } = await api.post("/pricing/portal");
                  if (data?.url) window.location.href = data.url;
                } catch (e) {
                  toast.error(
                    fmtErr(
                      e.response?.data?.detail,
                      "Le portail de paiement n’est pas encore connecté.",
                    ),
                  );
                }
              }}
              className="rounded-xl border px-3 py-2 text-sm font-medium hover:bg-secondary"
            >
              {t("account.gerer_mon_abonnement_changer_de_methode")}
            </button>
          </div>
        )}
        {user?.subscription_cancel_at_period_end && (
          <p className="text-sm text-amber-600 mt-3">
            {t("account.ton_abonnement_est_programme_pour_s")}
          </p>
        )}
      </Section>

      {user?.is_affiliate && (
        <Section title={t("account.espace_affilie")}>
          <div className="rounded-xl border border-primary/20 bg-primary/5 p-4">
            <p className="text-sm font-semibold">
              {t("account.ton_espace_partenaire_est_actif")}
            </p>
            <p className="mt-1 text-sm text-muted-foreground">
              {t("account.retrouve_ton_lien_tes_clics_les")}
            </p>
            <button
              onClick={() => nav("/affiliate")}
              className="mt-3 flex items-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground"
            >
              <UsersRound size={16} /> {t("account.ouvrir_mon_espace_affilie")}
            </button>
          </div>
        </Section>
      )}
      {!user?.is_affiliate && (
        <Section title={t("navbar.devenir_affilie")}>
          <div className="rounded-xl border border-primary/20 bg-primary/5 p-4">
            <p className="text-sm font-semibold">
              {t("account.recommande_harf_et_recois_50_des")}
            </p>
            <p className="mt-1 text-sm text-muted-foreground">
              {t("account.envoie_une_demande_avec_l_email")}
            </p>
            {user?.affiliate_application_status === "pending" && (
              <p className="mt-3 rounded-lg bg-secondary px-3 py-2 text-xs font-semibold">
                {t("account.ta_demande_est_en_cours_d")}
              </p>
            )}
            {user?.affiliate_application_status === "rejected" && (
              <p className="mt-3 rounded-lg bg-destructive/10 px-3 py-2 text-xs font-semibold text-destructive">
                {t("account.ta_demande_a_ete_refusee_tu")}
              </p>
            )}
            <button
              onClick={() => nav("/affiliate-program")}
              className="mt-3 flex items-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground"
            >
              {user?.affiliate_application_status === "pending"
                ? t("account.voir_ma_demande")
                : t("account.demander_a_devenir_affilie")}
            </button>
          </div>
        </Section>
      )}

      <Section title={t("account.objectif_quotidien")}>
        <div className="grid grid-cols-3 gap-3 mb-3">
          {[5, 10, 20].map((g) => (
            <button
              key={g}
              data-testid={`account-goal-${g}`}
              onClick={() => setGoal(g)}
              className={`py-4 rounded-xl border-2 transition ${goal === g ? "border-primary bg-primary/10" : "hover:border-primary/50"}`}
            >
              <div className="font-head text-xl font-bold">{g}</div>
              <div className="text-xs text-muted-foreground">min/jour</div>
            </button>
          ))}
        </div>
        <button
          data-testid="save-goal"
          onClick={saveGoal}
          className="w-full py-2.5 rounded-xl bg-primary text-primary-foreground font-medium"
        >
          {t("lessoneditor.enregistrer")}
        </button>
      </Section>

      {user?.provider !== "google" && (
        <>
          <Section title={t("account.changer_de_mot_de_passe")}>
            <Field
              type="password"
              placeholder={t("account.mot_de_passe_actuel")}
              data-testid="pw-current"
              value={pw.current}
              onChange={(e) => setPw({ ...pw, current: e.target.value })}
            />
            <Field
              type="password"
              placeholder={t("account.nouveau_mot_de_passe")}
              data-testid="pw-new"
              value={pw.new}
              onChange={(e) => setPw({ ...pw, new: e.target.value })}
            />
            <button
              data-testid="save-pw"
              onClick={changePw}
              className="w-full py-2.5 rounded-xl bg-primary text-primary-foreground font-medium"
            >
              {t("account.mettre_a_jour")}
            </button>
          </Section>
          <Section title={t("account.changer_d_email")}>
            <Field
              type="email"
              placeholder={t("account.nouvel_email")}
              data-testid="new-email"
              value={em.email}
              onChange={(e) => setEm({ ...em, email: e.target.value })}
            />
            <Field
              type="password"
              placeholder={t("account.mot_de_passe_confirmation")}
              data-testid="email-pw"
              value={em.password}
              onChange={(e) => setEm({ ...em, password: e.target.value })}
            />
            <button
              data-testid="save-email"
              onClick={changeEmail}
              className="w-full py-2.5 rounded-xl bg-primary text-primary-foreground font-medium"
            >
              {t("account.mettre_a_jour")}
            </button>
          </Section>
        </>
      )}

      <Section title={t("account.aide")}>
        <button
          data-testid="replay-tuto"
          onClick={replayTuto}
          className="w-full py-2.5 rounded-xl border font-medium hover:bg-secondary"
        >
          {t("navbar.revoir_le_tutoriel")}
        </button>
      </Section>

      <Section title={t("account.confidentialite_et_cookies")}>
        <p className="text-sm text-muted-foreground mb-3">
          {t("account.modifie_a_tout_moment_ton_choix")}
        </p>
        <button
          data-testid="cookie-settings"
          onClick={() => {
            localStorage.removeItem("harf_cookie_consent");
            window.dispatchEvent(new CustomEvent("harf:open-cookie-settings"));
          }}
          className="w-full py-2.5 rounded-xl border font-medium hover:bg-secondary"
        >
          {t("account.parametres_cookies")}
        </button>
      </Section>

      {user?.role !== "admin" && (
        <Section title={t("account.zone_de_danger")}>
          {!confirmDel ? (
            <button
              data-testid="delete-account"
              onClick={() => setConfirmDel(true)}
              className="w-full py-2.5 rounded-xl border-2 border-destructive text-destructive font-medium hover:bg-destructive/10"
            >
              {t("account.supprimer_mon_compte")}
            </button>
          ) : (
            <div className="space-y-2">
              <p className="text-sm text-destructive">
                {t("account.cette_action_est_irreversible_toute_ta")}
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setConfirmDel(false)}
                  className="flex-1 py-2.5 rounded-xl border font-medium"
                >
                  {t("groupui.annuler")}
                </button>
                <button
                  data-testid="confirm-delete"
                  onClick={del}
                  className="flex-1 py-2.5 rounded-xl bg-destructive text-destructive-foreground font-medium"
                >
                  {t("account.confirmer_la_suppression")}
                </button>
              </div>
            </div>
          )}
        </Section>
      )}
    </div>
  );
}
